import React, { Fragment, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { isToday, isSelected, isStartOfRange, isInRange, isEndOfRange, getHiddenEventCount, isFirstDayOfMonth, isInHiddenSelectionRange, isHiddenEventDay, isPast, isOverwrittenEvent, displayAsPast, isPastMonth, } from './utils';
import { CalendarEvent } from './CalendarEvent';
import styles from './EventCalendarDay.module.scss';
export const EventCalendarDay = ({ day, selection, isSelectable, onSelect, dayEventPropsMap, monthlySummary, }) => {
    const [dayEventProps, setDayEventProps] = useState({
        renderCustomEvent: undefined,
        dayStyles: {},
        events: [],
        inBetweenEvents: [],
    });
    const [hiddenEventsCount, setHiddenEventsCount] = useState(0);
    const [summary, setSummary] = useState();
    const dayStyles = useMemo(() => {
        const classes = [];
        if (!day.displayable) {
            classes.push('calendarDayHidden');
            if (isInHiddenSelectionRange(day, selection)) {
                classes.push('calendarDayInRange');
            }
            if (displayAsPast(day)) {
                classes.push('calendarDayHiddenPast');
            }
            return {
                classes,
                styles: undefined,
            };
        }
        classes.push('calendarDay');
        if (isToday(day.dateTime)) {
            classes.push('calendarDayToday');
        }
        if (isSelectable(day.dateTime)) {
            classes.push('calendarDaySelectable');
        }
        if (isSelected(day.dateTime, selection)) {
            classes.push('calendarDaySelected');
        }
        if (isStartOfRange(day.dateTime, selection)) {
            classes.push('calendarDayStartOfRange');
        }
        if (isInRange(day.dateTime, selection)) {
            classes.push('calendarDayInRange');
        }
        if (isEndOfRange(day.dateTime, selection)) {
            classes.push('calendarDayEndOfRange');
        }
        return {
            classes,
            styles: dayEventProps === null || dayEventProps === void 0 ? void 0 : dayEventProps.dayStyles,
        };
    }, [day, dayEventProps === null || dayEventProps === void 0 ? void 0 : dayEventProps.dayStyles, isSelectable, selection]);
    const handeDayClick = () => {
        if (!day.displayable) {
            return;
        }
        onSelect(day.dateTime);
    };
    useEffect(() => {
        const dayEventProps = dayEventPropsMap[day.dateTime.toFormat('yyyy-MM-dd')];
        setDayEventProps(dayEventProps);
        if (monthlySummary) {
            const summary = monthlySummary[day.dateTime.startOf('month').toFormat('yyyy-MM-dd')];
            setSummary(summary);
        }
        if (dayEventProps) {
            setHiddenEventsCount(getHiddenEventCount(dayEventProps.events));
        }
    }, [day, dayEventPropsMap, monthlySummary]);
    const toCurrency = (value) => value.toLocaleString('en-GB', {
        style: 'currency',
        currency: 'GBP',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    });
    return (<div className={clsx(styles.calendarDayWrapper, !day.displayable && styles.calendarDayWrapperHidden, !day.displayable && displayAsPast(day) && styles.calendarDayWrapperHiddenPast)}>
      {isFirstDayOfMonth(day.dateTime) && day.displayable && (<>
          <p className={styles.calendarDayMonthLabel}>{day.dateTime.toFormat('MMM')}</p>
          {summary && (<div className={clsx(styles.calendarBreakSummary, [1, 2, 3].includes(day.dateTime.weekday)
                    ? isPastMonth(day.dateTime)
                        ? styles.rightPosPast
                        : styles.rightPos
                    : styles.leftPos, typeof summary.flexiValues === 'object' ? styles.flexi : styles.fixed)}>
              <p>
                {isPastMonth(day.dateTime) ? 'You could have earned ' : 'Earn up to '}
                {typeof summary.flexiValues === 'object' ? (<span>
                    {toCurrency(summary.flexiValues.min)} - {toCurrency(summary.flexiValues.max)}
                  </span>) : (<span>{toCurrency(summary.values) || 0}</span>)}
              </p>
              <p>({summary.breakCount || 0} breaks)</p>
            </div>)}
        </>)}
      <div style={dayStyles.styles} className={clsx(dayStyles.classes.map((c) => styles[c]))} onClick={handeDayClick} role='presentation' data-testid='calendar-day'>
        <span>
          {day.displayable
            ? day.dateTime.day.toLocaleString('en-GB', {
                minimumIntegerDigits: 2,
                useGrouping: false,
            })
            : ''}
        </span>
        {dayEventProps === null || dayEventProps === void 0 ? void 0 : dayEventProps.events.map((eventProps) => {
            var _a;
            return (<Fragment key={`${eventProps.id}-${eventProps.eventPosition}-${eventProps.dayPosition}-${day.dateTime.toISO()}`}>
            {!isHiddenEventDay(day, eventProps) &&
                    !isOverwrittenEvent(eventProps) &&
                    eventProps.slot !== -1 && (<CalendarEvent isPastEvent={isPast(day.dateTime)} labelBackground={(_a = dayStyles.styles) === null || _a === void 0 ? void 0 : _a.background} {...eventProps}/>)}
          </Fragment>);
        })}
        {dayEventProps === null || dayEventProps === void 0 ? void 0 : dayEventProps.inBetweenEvents.map((eventProps) => {
            var _a;
            return (<Fragment key={`${eventProps.id}-${eventProps.eventPosition}-${eventProps.dayPosition}-${day.dateTime.toISO()}`}>
            {!isHiddenEventDay(day, eventProps) &&
                    !isOverwrittenEvent(eventProps) &&
                    eventProps.slot !== -1 && (<CalendarEvent isPastEvent={isPast(day.dateTime)} labelBackground={(_a = dayStyles.styles) === null || _a === void 0 ? void 0 : _a.background} {...eventProps}/>)}
          </Fragment>);
        })}
        {hiddenEventsCount > 0 && day.displayable && (<div className={clsx(styles.calendarDayHiddenEvents, isPast(day.dateTime) && styles.calendarDayHiddenEventsPast)}>
            {`+${hiddenEventsCount}`}
          </div>)}
      </div>
    </div>);
};
